import { Directive, ElementRef, HostListener, forwardRef, Renderer2, inject } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { KeyCodes } from '../../../constants';
import { FormsValidators } from '../validators';
import { FormatService } from '../../../services';

const noop = () => {};

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CurrencyMaskDirective),
  multi: true,
};

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[currencyMask]',
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
})
export class CurrencyMaskDirective implements ControlValueAccessor {
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly formatService = inject(FormatService);
  private readonly el: HTMLInputElement;
  private modelValue: any;

  constructor() {
    const elementRef = this.elementRef;

    this.el = elementRef.nativeElement;
  }

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (a: any) => void = noop;

  @HostListener('focus', ['$event.target.value'])
  onfocus(value: any) {
    const valid = FormsValidators.greaterThanZero(value);
    if (!valid) {
      this.el.value = '';
    }
  }

  @HostListener('blur', ['$event.target.value'])
  onBlur(value: any) {
    this.onTouchedCallback();
    const valid = FormsValidators.greaterThanZero(value);
    if (!valid) {
      this.el.value = '0';
      this.modelValue = 0;
    } else {
      this.el.value = this.format(value);
      this.modelValue = this.parse(this.el.value);
      this.onChangeCallback(this.modelValue);
      if (this.modelValue) {
        this.renderer.setAttribute(this.elementRef.nativeElement, 'value', this.modelValue);
      }
    }
  }

  @HostListener('change', ['$event.target.value'])
  onChange(value: any) {
    this.el.value = this.format(value);
    this.modelValue = this.parse(this.el.value);
    this.onChangeCallback(this.modelValue);
    if (this.modelValue) {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'value', this.modelValue);
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: any) {
    this.preventInvalidEntry(event);
  }

  @HostListener('keyup', ['$event'])
  onKeyUp(event: any) {
    this.preventInvalidEntry(event);

    const key = event.which ?? event.keyCode ?? 0;

    let value = event.target.value.replace(/,/g, '');

    if (key === KeyCodes.UpArrow) {
      value = +value + 1;
    } else if (key === KeyCodes.DownArrow) {
      if (+value > 0) {
        value = +value - 1;
      }
    }

    this.el.value = this.format(value.toString());
    this.modelValue = this.parse(this.el.value);
    this.onChangeCallback(this.modelValue);
    if (this.modelValue) {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'value', this.modelValue);
    }
  }

  get value(): any {
    return this.modelValue;
  }
  set value(v: any) {
    if (v !== this.modelValue) {
      this.modelValue = v;
      this.onChangeCallback(v);
    }
  }

  writeValue(value: any) {
    if (value !== this.modelValue) {
      this.el.value = this.format(value);
      if (value) {
        this.renderer.setAttribute(this.elementRef.nativeElement, 'value', value);
      }
      this.modelValue = value;
    }
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  // To display value
  private format(value: string) {
    return this.formatService.formatCurrency(value);
  }

  // To model
  private parse(value: string) {
    value = value.replace(/[^\d\.]/g, '');
    return value;
  }

  private preventInvalidEntry(event: any) {
    const key = event.which ?? event.keyCode ?? 0;

    if (
      key === KeyCodes.Insert ||
      (key !== KeyCodes.Delete &&
        key >= KeyCodes.Space &&
        !(
          (key >= KeyCodes.Zero && key <= KeyCodes.Nine) ||
          (key >= KeyCodes.ZeroNumpad && key <= KeyCodes.NineNumpad) ||
          key === KeyCodes.LeftArrow ||
          key === KeyCodes.RightArrow
        ))
    ) {
      event.preventDefault();
    }
  }
}
